import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSingleEffect } from 'react-haiku';
import { useCollect } from 'hooks';
import { saveAs } from 'file-saver';
import excel from 'exceljs';

import DayJs from 'utils/Dayjs';

import { GRADE_STUDENT_STATES } from 'utils/Constants';

import Pages from 'components/Misc/Pages';
import { Button } from 'components/Misc/Form';

import Guide from 'containers/Administrator/Grades/Profile/Journal/Guide';
import Student from 'containers/Administrator/Grades/Profile/Journal/Student';

import { useGradeJournalStore } from 'store/grade/useGradeJournalStore';
import { useGradeProfileStore } from 'store/grade/useGradeProfileStore';

const Journal = ({ tab, setTab }) => {
	// Use routers params
	const { id } = useParams();

	// Update selected tab
	useSingleEffect(() => setTab(tab));

	// Use grade student store
	const grade = useGradeProfileStore(state => state.grade);
	const [result, search, parameters, onSearch, onInit, onReset] = useGradeJournalStore(state => [
		state.result,
		state.search,
		state.parameters,
		state.onSearch,
		state.onInit,
		state.onReset
	]);

	// Store requested grade's students
	const [, collect] = useCollect(`grades/${id}`, parameters, onInit);
	const [[, progress], collectAll] = useCollect(`grades/${id}`, { related: 'journal' });

	const onDownloadJournal = async () => {
		// Collect all student's in grade
		const data = await collectAll();

		// Create a workbook
		const workbook = new excel.Workbook();

		// Create a worksheet with frozen first row and first two columns
		const worksheet = workbook.addWorksheet('Klase', {
			views: [{
				state: 'frozen',
				xSplit: 3,
				ySplit: 1
			}]
		});

		// Create heading columns
		worksheet.columns = [
			{
				key: 'name',
				header: 'Vārds',
				width: 24,
				style: {
					font: {
						size: 16,
						bold: true
					},
					alignment: {
						vertical: 'middle',
						horizontal: 'center'
					}
				}
			},
			{
				key: 'surname',
				header: 'Uzvārds',
				width: 24,
				style: {
					font: {
						size: 16,
						bold: true
					},
					alignment: {
						vertical: 'middle',
						horizontal: 'center'
					}
				}
			},
			{
				key: 'state',
				header: 'Statuss',
				width: 16,
				style: {
					font: {
						size: 16,
						bold: true
					},
					alignment: {
						vertical: 'middle',
						horizontal: 'center'
					}
				}
			},
			...data.subjects.map(subject => ({ 
				key: subject.id,
				header: subject.title,
				width: 8,
				style: {
					font: {
						size: 16,
						bold: true
					},
					alignment: {
						vertical: 'left',
						horizontal: 'center',
						textRotation: '90'
					}
				}
			})),
			{
				key: 'warrant',
				header: 'Rīkojums',
				width: 64,
				style: {
					font: {
						size: 16,
						bold: true
					},
					alignment: {
						vertical: 'middle',
						horizontal: 'center'
					}
				}
			},
		];

		// Create data rows
		const rows = data.students.map(student => ([
			student.name,
			student.surname,
			GRADE_STUDENT_STATES.find(state => state.value === student?.graderel?.state)?.text,
			...data.subjects.map(subject => {
				const assigned = student.subjects.find(s => s.id === subject.id);

				// Return empty row if not assigned
				if (!assigned) return '';

				// Return nv if assigned but no mark
				if (!assigned.mark) return 'nv';

				// Return mark as String:mark or Number:mark
				return assigned.mark.mark === 'nv'
					? assigned.mark.mark
					: Number(assigned.mark.mark);
			}),
			student?.report?.warrant ?? ''
		]));

		// Append rows to sheet
		worksheet.addRows(rows);
		
		// Update cell styles based on value
		worksheet.eachRow((row, rowNumber) => {
			row.eachCell((cell, colNumber) => {
				if (rowNumber > 1) {
					// Set row height for every except first row
					row.height = 24;

					cell.style = {
						...cell.style,
						font: {
							...cell.style.font,
							bold: false
						},
						alignment: {
							...cell.style.alignment,
							vertical: 'middle',
							horizontal: 'center',
							textRotation: '0'
						}
					};
	
					if (['nv', 1, 2, 3].includes(cell.value)) {
						cell.style = {
							...cell.style,
							font: {
								...cell.style.font,
								color: {
									argb: 'fff43f5e'
								}
							}
						};
					}
	
					if (row.cellCount === colNumber) {
						cell.style = {
							...cell.style,
							alignment: {
								...cell.style.alignment,
								horizontal: 'left'
							}
						};
					}

					if (colNumber === 1 || colNumber === 2) {
						cell.style = {
							...cell.style,
							font: {
								...cell.style.font,
								bold: true
							},
							alignment: {
								...cell.style.alignment,
								horizontal: 'left'
							}
						};
					}
				}

				cell.style = {
					...cell.style,
					border: {
						top: {
							style: 'thin'
						},
						left: {
							style: 'thin'
						},
						bottom: {
							style: 'thin'
						},
						right: {
							style: 'thin'
						}
					}
				};
			});
		});
		
		// Create excel buffer
		const buffer = await workbook.xlsx.writeBuffer();
		
		// Make a blob from buffer
		const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

		// Safe buffer as xlsx file
		saveAs(blob, `${grade.title} - ${DayJs().format('YYYY-MM-DD')}.xlsx`);
	};

	// Collect tasks on mount and on parameter change
	useEffect(() => {
		if (id) (async () => await collect())();
	}, [id, collect]);

	// Reset journal's state
	useEffect(() => {
		return () => {
			onReset();
		};
	}, []);

	return (
		<div className="mt-4 p-4 space-y-4 bg-white dark:bg-neutral-800 rounded-lg transition-colors">
			<div className="flex justify-end">
				<Button
					square
					icon="download"
					progress={progress}
					onClick={onDownloadJournal}
				/>
			</div>

			{/* Table design */}
			<div className="overflow-x-auto">
				<table className="relative border-separate border-spacing-0 w-full table-fixed">
					<thead>
						<tr>
							<th className="sticky left-0 z-10 w-56 bg-white dark:bg-neutral-800 p-2 pl-3 first:border-l first:rounded-tl-lg last:rounded-tr-lg border-r border-b border-t border-neutral-100 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 whitespace-nowrap"></th>
							{result.subjects.map(subject =>
								<th key={subject.id} className="bg-white dark:bg-neutral-800 w-11 py-3 first:border-l first:rounded-tl-lg last:rounded-tr-lg border-r border-b border-t border-neutral-100 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 whitespace-nowrap">
									<span className="inline-block rotate-180" style={{ writingMode: 'vertical-lr' }}>{subject.title}</span>
								</th>
							)}
						</tr>
					</thead>
					<tbody>
						{result.students.map(student =>
							<Student key={student.id} student={student} />
						)}
					</tbody>
				</table>
			</div>
			
			{/* Pagination */}
			<Pages
				total={result.total}
				active={search.page}
				onChange={onSearch}
			/>
			
			{/* Mark style guide */}
			<Guide />
		</div>
	);
};

export default Journal;
